/* eslint-disable no-underscore-dangle */
import {openRuliuGroup} from '@/utils/browserUtils';
import * as store from '@/store';
import {
    DEFAULT_CODE_PREVIEW_COMPONENT_HEIGHT,
    DEFAULT_CODE_PREVIEW_COMPONENT_WIDTH,
    DEFAULT_CUSTOM_INIT_HEIGHT,
    DEFAULT_CUSTOM_INIT_HEIGHT_IN_CARD,
    DEFAULT_CUSTOM_INIT_WIDTH,
    RuliuGroupId,
    FlexBuilderType,
    FlexBuilderTypeToNodeTypeMap,
} from '../constant';
import {IGetBuilderLockResponse, IGetTemplateResponse} from '../render/request.type';
import {EntryFile} from '../render/constant';
import type {FloatingBarConfig} from '@baidu/realtime-editor';
import BtnMergeCardSvg from '../icon/btn-merge-card.svg?tsx';
import BtnSplitCardSvg from '../icon/btn-split-card.svg?tsx';
import BtnNotificationSvg from '../icon/btn-notification-config.svg?tsx';
import eventBus, {CHANGE_FLEX_BUILDER_CARD_STATUS, EDIT_FLEX_BUILDER, FLEX_BUILDER_NOTIFICATION} from '@/eventBus';

/**
 * 获取默认的模板列表组件高度
 * @param isCard 是否随心搭卡片模式
 */
export const getDefaultCustomInitHeight = (isCard: boolean) => {
    return isCard ? DEFAULT_CUSTOM_INIT_HEIGHT_IN_CARD : DEFAULT_CUSTOM_INIT_HEIGHT;
};

/**
 * 创建点击插入菜单后的回调对象值，即节点数据
 */
export const createOnInsertResult = async (
    type: 'custom' | 'template',
    builderId = '',
    isCard = false,
    flexBuilderType = FlexBuilderType.frontend
) => {
    const node = {
        type: FlexBuilderTypeToNodeTypeMap[flexBuilderType],
        builderId,
        boxSize: {
            width: 0,
            height: 0,
        },
        children: [{text: ''}],
    };

    if (type === 'template') {
        node.boxSize.width = DEFAULT_CODE_PREVIEW_COMPONENT_WIDTH;
        node.boxSize.height = DEFAULT_CODE_PREVIEW_COMPONENT_HEIGHT;
        return node;
    }

    node.boxSize.width = DEFAULT_CUSTOM_INIT_WIDTH;
    node.boxSize.height = getDefaultCustomInitHeight(isCard);
    return node;
};

/**
 * 从【查询 Builder】接口和【查询模板详情】接口返回数据中解析出 code
 * @param res 接口返回数据
 */
export const getCode = (res: IGetTemplateResponse) => {
    const code = res?.content?.[0]?.code || '';
    return code;
};

/**
 * 从【查询 Builder】接口和【查询模板详情】接口返回数据中解析出 compiledInfo
 * @param res 接口返回数据
 */
export const getCompiledInfo = (res: IGetTemplateResponse) => {
    const compiledInfo = {
        compiledCode: res?.output?.[EntryFile]?.code || '',
        dependencies: res?.output?.[EntryFile]?.dependencies || [],
        style: res?.output?.[EntryFile]?.style || '',
    };
    return compiledInfo;
};

export function generateUUID(length?: number): string {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    if (!length || length <= 0) {
        return uuid;
    }
    return uuid.slice(0, length);
}

/**
 * 从【查询 Builder】接口和【查询模板详情】接口返回数据中解析出 title
 * @param res 接口返回数据
 */
export const getTitle = (res: IGetTemplateResponse) => {
    const title = res?.title || res?.content?.[0]?.title || '';
    return title;
};

/**
 * 打开FlexBuilder使用说明文档
 *
 */
export const openFlexBuilderInstructionDoc = () => {
    window.open('https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/2tsPs8CtSd/Bu7DDg4dpB/mfrhJmtqNgXEoi');
};

/**
* 打开随心搭如流群组
*/
export const openFlexBuilderRuLiuGroup = () => {
    openRuliuGroup(RuliuGroupId);
};

/**
 * 获取编辑按钮文案
 */
const getEditBtnTitle = ({
    isEditing,
    iAmEditing,
    isKuShareMode,
    lockInfo,
}: {
    isEditing: boolean;
    iAmEditing: boolean;
    isKuShareMode: boolean;
    lockInfo?: IGetBuilderLockResponse;
}) => {
    if (isEditing && !iAmEditing) {
        return `${lockInfo?.nickname} 正在沟通中`;
    }
    if (isKuShareMode) {
        return 'KU享模式不支持编辑';
    }
    return '编辑';
};

/**
 * 获取编辑随心搭按钮的悬浮菜单配置
 */
export const getFloatingBarConfigOfEdit = ({
    builderId,
    isEditing,
    iAmEditing,
    isKuShareMode,
    readonly,
    lockInfo,
}: {
    builderId: string;
    isEditing: boolean;
    iAmEditing: boolean;
    isKuShareMode: boolean;
    readonly: boolean;
    lockInfo?: IGetBuilderLockResponse;
}): FloatingBarConfig => {
    return {
        type: 'button',
        icon: 'diagramEdit',
        disabled: isEditing && !iAmEditing || isKuShareMode,
        title: getEditBtnTitle({
            isEditing,
            iAmEditing,
            isKuShareMode,
            lockInfo,
        }),
        hidden: readonly,
        onClick: () => {
            eventBus.emit(EDIT_FLEX_BUILDER, builderId);
        },
    };
};

/**
 * 获取随心搭独立成卡按钮的悬浮菜单配置
 */
export const getFloatingBarConfigOfCardMode = ({
    builderId,
    isCard,
    readonly,
    isInLayoutContainer,
    isCardView,
}: {
    builderId: string;
    isCard: boolean;
    readonly: boolean;
    isInLayoutContainer: boolean;
    isCardView: boolean;
}): FloatingBarConfig => {
    return {
        type: 'button',
        icon: () => {
            // TODO: 不知道为何有 ts 错误提示，但是实际上不影响编译，可暂时忽略
            return isCard ? <BtnMergeCardSvg /> : <BtnSplitCardSvg />;
        },
        title: isInLayoutContainer ? '此处不可独立成卡' : (isCard ? '取消独立成卡' : '独立成卡'),
        // 在布局容器内，不支持独立成卡
        // 在文档视图下，隐藏入口
        hidden: readonly || !isCardView,
        disabled: isInLayoutContainer,
        className: 'mp-btn-responsive',
        onClick: () => {
            eventBus.emit(CHANGE_FLEX_BUILDER_CARD_STATUS, builderId);
        },
    };
};

/**
 * 获取随心搭通知设置按钮的悬浮菜单配置
 */

export const getFloatingBarConfigOfNotificationConfig = ({builderId}: {
    builderId:string;
}): FloatingBarConfig => {
    return {
        type: 'button',
        icon: () => {
            return <BtnNotificationSvg />;
        },
        title: '通知设置',
        hidden: false,
        disabled: false,
        className: 'mp-btn-responsive',
        onClick: () => {
            eventBus.emit(FLEX_BUILDER_NOTIFICATION, builderId);
        },
    };
};

/**
 * 获取是否正在编辑中的状态
 * @param lockInfo 锁信息
 */
export const getIsEditing = (lockInfo?: IGetBuilderLockResponse): boolean => {
    return Boolean(lockInfo?.locked);
};

/**
 * 获取是否当前用户正在编辑中的状态
 * @param userGuid 用户 id
 * @param lockInfo 锁信息
 */
export const getIAmEditing = (
    userGuid: string,
    lockInfo?: IGetBuilderLockResponse
): boolean => {
    return getIsEditing(lockInfo) && (lockInfo?.createdBy === userGuid);
};

/**
 * 获取是否为随心搭卡片模式
 */
export const getIsCard = ({
    cardType,
    asCardChild,
}: {
    cardType: string;
    asCardChild: boolean;
}): boolean => {
    return cardType === 'full' && asCardChild;
};

/**
 * 获取是否在布局容器内
 */
export const getIsInLayoutContainer = (
    asCardChild: boolean
): boolean => {
    return !asCardChild;
};

/**
 * 获取是否代码编辑器已生成的状态
 */
export const getBuilderAlive = (
    builderId?: string
): boolean => {
    return Boolean(builderId);
};

/**
 * 获取是否代码编辑器已生成，且组件没有代码的状态
 */
export const getBuilderAliveAndNoCode = (
    builderId?: string,
    hasCode?: boolean
): boolean => {
    const builderAlive = getBuilderAlive(builderId);
    return Boolean(builderAlive && !hasCode);
};

/**
 * 获取代码编辑器已生成，且组件没有代码，且没有被编辑过的状态
 */
export const getBuilderAliveAndNoCodeAndHasNotEdited = (
    builderId?: string,
    hasCode?: boolean,
    hasEdited?: boolean
): boolean => {
    const builderAliveAndNoCode = getBuilderAliveAndNoCode(builderId, hasCode);
    return builderAliveAndNoCode && !hasEdited;
};

/**
 * 获取是否显示随心搭 Logo 的状态
 */
export const getIsShowLogo = ({
    builderId,
    hasCode,
    hasEdited,
}: {
    builderId?: string;
    hasCode?: boolean;
    hasEdited?: boolean;
}): boolean => {
    const builderAlive = getBuilderAlive(builderId);
    const builderAliveAndNoCodeAndHasNotEdited = getBuilderAliveAndNoCodeAndHasNotEdited(
        builderId,
        hasCode,
        hasEdited
    );
    return builderAlive && !builderAliveAndNoCodeAndHasNotEdited;
};

interface IFlexBuilderClipboard {
    builderId: string;
    docGuid: string;
    isPasted: boolean; // 是否被粘贴过
}

// 随心搭剪切板对象，用于存储剪切信息
export const flexBuilderClipboard = {
    val: null as IFlexBuilderClipboard | null,
    get: () => {
        return flexBuilderClipboard.val;
    },
    set: (id: string) => {
        if (!id) {
            return;
        }

        const docGuid = store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '';
        flexBuilderClipboard.val = {
            builderId: id,
            docGuid,
            isPasted: false,
        };
    },
    finishFirstPaste: () => {
        if (flexBuilderClipboard.val) {
            flexBuilderClipboard.val.isPasted = true;
        }
    },
    clear: () => {
        flexBuilderClipboard.val = null;
    },
};

// 随心搭粘贴对象，用于存储粘贴信息
export const flexBuilderPaste = {
    val: [] as string[],
    isExist: (id: string) => {
        return flexBuilderPaste.val.includes(id);
    },
    add: (id: string) => {
        if (!id) {
            return;
        }
        flexBuilderPaste.val.push(id);
    },
    delete: (id: string) => {
        flexBuilderPaste.val = flexBuilderPaste.val.filter(item => item !== id);
    },
    clear: () => {
        flexBuilderPaste.val = [];
    },
};

/**
 * 复制时携带 KV 缓存场景
 */
export const copyKvStorageObj = {
    limit: 50,
    storageKey: 'flexBuilderCopyKvConfig',
    get(): string[] {
        try {
            const data = localStorage.getItem(copyKvStorageObj.storageKey);
            return data ? JSON.parse(data) : [];
        } catch (e) {
            return [];
        }
    },
    has(id: string) {
        return copyKvStorageObj.get()?.includes(id);
    },
    set: (id: string) => {
        const ids = copyKvStorageObj.get();

        // 如果已存在，先移除（确保最新在最后）
        const index = ids.indexOf(id);
        if (index !== -1) {
            ids.splice(index, 1);
        }

        // 超出上限，删除最早添加的
        if (ids.length >= copyKvStorageObj.limit) {
            ids.shift();
        }

        ids.push(id);
        copyKvStorageObj._save(ids);
    },
    remove(id: string) {
        const ids = copyKvStorageObj.get();
        const newIds = ids.filter(item => item !== id);
        copyKvStorageObj._save(newIds);
    },
    _save(ids: string[]) {
        localStorage.setItem(copyKvStorageObj.storageKey, JSON.stringify(ids));
    },
};

interface IEnvItem {
    builderId: string;
    envList: Array<{
        key: string;
        value: string;
    }>;
}

/**
 * 环境变量缓存场景（内部验证使用）
 */
export const envStorageObj = {
    limit: 50,
    storageKey: 'flexBuilderEnvConfig',
    get(): IEnvItem[] {
        try {
            const data = localStorage.getItem(envStorageObj.storageKey);
            return data ? JSON.parse(data) : [];
        } catch (e) {
            return [];
        }
    },
    getByBuilderId(builderId: string) {
        const list = envStorageObj.get();
        const found = list.find(item => item.builderId === builderId);
        return found ? found.envList : null;
    },
    set: (builderId: string, envList: IEnvItem['envList']) => {
        const list = envStorageObj.get();

        const found = list.find(item => item.builderId === builderId);
        if (found) {
            found.envList = envList;
        } else {
            if (list.length >= 10) {
                list.shift();
            }

            list.push({builderId, envList});
        }

        localStorage.setItem(envStorageObj.storageKey, JSON.stringify(list));
    },
};

/**
 * 向 WeirWood 系统上报自定义错误
 *
 * @param error - 错误对象
 * @param builderId - 构建器ID
 * @param prefix - 错误前缀，默认为'随心搭错误'
 */
export function reportCustomErrorToWeirWood({
    error,
    builderId,
    prefix = '随心搭错误',
}: {
    error: Error;
    builderId: string;
    prefix?: string;
}) {
    if (error instanceof Error) {
        error.message = `[${prefix}] ${builderId}-${error.message}`;
    }
    console.error(error);
}

export function parseJson(json: string): any {
    let parseJson: any = undefined;
    try {
        parseJson = JSON.parse(json);
    } catch (error) {
        // 调试可用：
        // console.error('[parseJson] parse json error: ', (error as unknown as Error)?.message);
    }
    return parseJson;
}
